import { ExternalLink, LogOut } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { debounce } from 'lodash';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { getTenantSourceName } from '../../tenants';
import { FormSection } from '../../components/form-section';
import { Spinner } from '../../components/spinner';
import { Label } from '../../components/label';
import { Select } from '../../components/select';
import {
    TenantDto,
    TenantPageEntrySupportDto
} from '../../core/api/TenantClient';
import { ConfirmationModal } from '../../components/confirmation-modal';
import { EmptyTextField } from '../../components/textfield';
import { DeleteModal } from './delete-modal';
import { useStore } from './store';
import { TenantActivateModal } from '../../tenants/modals/tenant-activate-modal';
import { TenantDeactivateModal } from '../../tenants/modals/tenant-deactivate-modal';

const OpenLinkButton = ({ href }: { href: string }) =>
    href ? (
        <button
            type="button"
            aria-label="Open link"
            className="hover:text-foreground"
            onClick={() => window.open(href, '_blank')?.focus()}
        >
            <ExternalLink className="size-4" />
        </button>
    ) : null;

export const TenantDetail = observer(() => {
    const {
        googleUserAuth,
        tenantTypes,
        updateTenantType,
        corporateFeatures,
        corporateClient,
        tenantName,
        fields,
        updateTenant
    } = useStore();
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
    const [tenantTypeConfirmOpen, setTenantTypeConfirmOpen] =
        React.useState(false);
    const [tenantTypeChange, setTenantTypeChange] = React.useState<
        TenantDto['type'] | undefined
    >();

    const openDeactivateState = React.useState(false);
    const [, setDeactivateModalOpen] = openDeactivateState;

    const openActivateState = React.useState(false);
    const [, setActivateModalOpen] = openActivateState;

    const handleLogin = async () => {
        const session =
            await corporateClient.support.corporateSupportControllerCreateSession(
                {
                    email: googleUserAuth?.email,
                    firstName: googleUserAuth?.firstName,
                    lastName: googleUserAuth?.lastName
                }
            );
        if (session.data.redirectUrl) {
            window.open(session.data.redirectUrl, '_blank');
        }
    };
    const handleTenantChange = async (
        type: TenantPageEntrySupportDto['type']
    ) => {
        setTenantTypeChange(type);
        setTenantTypeConfirmOpen(true);
    };
    const handleTenantUpdate = async () => {
        const tenantType = await updateTenantType.request([tenantTypeChange]);
        await corporateFeatures.request([tenantName]);
        return tenantType;
    };

    const updateTenantCallback = React.useCallback(
        debounce((data: typeof fields) => {
            updateTenant.request([data]);
        }, 300),
        [updateTenant]
    );

    const editableField = (
        field: typeof fields.hubspotLink,
        label: string,
        link?: boolean
    ) => (
        <div className="flex flex-col gap-1.5">
            <Label>{label}</Label>
            <EmptyTextField
                type="text"
                value={field.value}
                InputProps={{
                    endAdornment: link ? (
                        <OpenLinkButton href={field.value} />
                    ) : undefined
                }}
                onChange={(val: string) => {
                    field.onChange(val);
                    updateTenantCallback(fields);
                }}
            />
        </div>
    );

    return (
        <>
            {corporateFeatures.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                fetched: ({ company, tenant }) => (
                    <div className="flex flex-col gap-6">
                        <TenantDeactivateModal
                            openState={openDeactivateState}
                            tenant={tenant}
                            onSuccess={() =>
                                void corporateFeatures.request([tenantName])
                            }
                        />
                        <TenantActivateModal
                            openState={openActivateState}
                            tenant={tenant}
                            onSuccess={() =>
                                corporateFeatures.request([tenantName])
                            }
                        />
                        <FormSection
                            title="Company"
                            actions={
                                <Button onClick={() => void handleLogin()}>
                                    Login
                                    <LogOut />
                                </Button>
                            }
                        >
                            <dl className="grid grid-cols-[160px_1fr] items-center gap-y-2 text-sm">
                                <dt className="text-muted-foreground">
                                    Company Name
                                </dt>
                                <dd>{company.name}</dd>
                                <dt className="text-muted-foreground">
                                    Source
                                </dt>
                                <dd>{getTenantSourceName(tenant.source)}</dd>
                                <dt className="text-muted-foreground">
                                    Participants
                                </dt>
                                <dd>{company.employeeCount}</dd>
                                <dt className="text-muted-foreground">Type</dt>
                                <dd className="max-w-xs">
                                    <Select
                                        label="Tenant Type"
                                        value={tenant.type}
                                        onChange={(val) => {
                                            handleTenantChange(val);
                                        }}
                                        options={tenantTypes.$ || []}
                                        noLabel
                                        fullWidth
                                    />
                                </dd>
                            </dl>
                        </FormSection>
                        <FormSection
                            title="Integrations"
                            description="Changes are saved automatically"
                        >
                            <div className="grid gap-4 md:grid-cols-2">
                                {editableField(
                                    fields.hubspotLink,
                                    'Hubspot link',
                                    true
                                )}
                                {editableField(
                                    fields.lexofficeLink,
                                    'Lexoffice link',
                                    true
                                )}
                                {editableField(
                                    fields.whitelabelId,
                                    'Whitelabel ID'
                                )}
                                {editableField(
                                    fields.whitelabelCurrencyCode,
                                    'Whitelabel CurrencyCode'
                                )}
                            </div>
                        </FormSection>
                        <FormSection title="Address">
                            <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-sm">
                                <dt className="text-muted-foreground">
                                    Street
                                </dt>
                                <dd>{company?.address?.street}</dd>
                                <dt className="text-muted-foreground">
                                    Number
                                </dt>
                                <dd>{company?.address?.houseNumber}</dd>
                                <dt className="text-muted-foreground">
                                    Postal code
                                </dt>
                                <dd>{company?.address?.zipCode}</dd>
                                <dt className="text-muted-foreground">City</dt>
                                <dd>{company?.address?.city}</dd>
                                <dt className="text-muted-foreground">
                                    Country
                                </dt>
                                <dd>{company?.address?.country}</dd>
                            </dl>
                        </FormSection>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="destructive"
                                onClick={() => void setDeleteModalOpen(true)}
                            >
                                Delete
                            </Button>
                            {tenant.status === 'ACTIVE' && (
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        void setDeactivateModalOpen(true)
                                    }
                                >
                                    Deactivate
                                </Button>
                            )}
                            {tenant.status === 'INACTIVE' && (
                                <Button
                                    onClick={() =>
                                        void setActivateModalOpen(true)
                                    }
                                >
                                    Activate
                                </Button>
                            )}
                        </div>
                        <DeleteModal
                            open={deleteModalOpen}
                            onModalClose={() => void setDeleteModalOpen(false)}
                        />
                        <ConfirmationModal
                            open={tenantTypeConfirmOpen}
                            submitHandler={handleTenantUpdate}
                            onModalClose={() =>
                                void setTenantTypeConfirmOpen(false)
                            }
                        >
                            <p className="text-lg font-semibold">
                                Are you sure you want to change the tenant type?
                            </p>
                            <Alert variant="warning">
                                <AlertDescription>
                                    This process causes severe changes to the
                                    product
                                </AlertDescription>
                            </Alert>
                        </ConfirmationModal>
                    </div>
                )
            })}
        </>
    );
});

export default TenantDetail;
